/**
 * Centralized configuration management with proper typing
 * Reads from runtime config (injected at container startup) first, then falls back to build-time env vars
 */

// Import types
import type { EnvironmentConfig as _EnvironmentConfig } from '../types/global';

/**
 * Get configuration value with runtime override support
 * Prioritizes window.__RUNTIME_CONFIG__ over import.meta.env for container runtime flexibility
 */
function getConfigValue(key: keyof RuntimeConfig): string | undefined {
  // First check runtime config (injected by docker-entrypoint.sh)
  const runtimeValue = window.__RUNTIME_CONFIG__?.[key];
  if (runtimeValue && runtimeValue !== '') {
    return runtimeValue;
  }

  // Fall back to build-time environment variable
  return import.meta.env[key] as string | undefined;
}

// Environment configuration with validation
export const config = {
  // OIDC Configuration
  authAuthority: getConfigValue('VITE_AUTH_AUTHORITY') as string,     // https://auth.agion.dev
  oidcIssuer: getConfigValue('VITE_OIDC_ISSUER') as string,           // https://auth.agion.dev

  // API endpoints
  apiUrl: getConfigValue('VITE_API_URL') as string,                   // https://api.agion.dev
  authServiceUrl: getConfigValue('VITE_AUTH_SERVICE_URL') as string,  // https://auth.agion.dev

  // Callback configuration
  redirectUri: getConfigValue('VITE_REDIRECT_URI') as string,         // https://login.agion.dev/callback

  // Domain configuration (environment-agnostic)
  authDomain: getConfigValue('VITE_AUTH_DOMAIN') as string,
  loginDomain: getConfigValue('VITE_LOGIN_DOMAIN') as string,

  // Production dashboard URL
  adminDashboardUrl: getConfigValue('VITE_ADMIN_DASHBOARD_URL') as string,

  environment: (getConfigValue('VITE_ENVIRONMENT') as 'development' | 'staging' | 'production') || 'production',

  // Observability
  sentryDsn: import.meta.env.VITE_SENTRY_DSN,
  googleAnalyticsId: import.meta.env.VITE_GOOGLE_ANALYTICS_ID,
  debugMode: getConfigValue('VITE_DEBUG_MODE') === 'true' || (import.meta.env.DEV && import.meta.env.NODE_ENV !== 'production'),
  featureFlags: parseFeatureFlags(import.meta.env.VITE_FEATURE_FLAGS),
  version: getConfigValue('VITE_APP_VERSION') || '1.0.0',
  buildTime: import.meta.env.VITE_BUILD_TIME || new Date().toISOString(),
};

function parseFeatureFlags(flagsString?: string): Record<string, boolean> {
  if (!flagsString) {
    return {
      enablePWA: true,
      enableAnalytics: true,
      enableMFA: true,
      enableMagicLink: true,
      enableWebAuthn: true,
      enableSocialLogin: true,
    };
  }

  try {
    return JSON.parse(flagsString);
  } catch {
    console.warn('Invalid feature flags JSON, using defaults');
    return {
      enablePWA: true,
      enableAnalytics: true,
      enableMFA: true,
      enableMagicLink: true,
      enableWebAuthn: true,
      enableSocialLogin: true,
    };
  }
}

// Configuration validation
export function validateConfig(): void {
  // Check if actual config values are set (not just env vars, since we have defaults)
  const configChecks = [
    { name: 'authAuthority', value: config.authAuthority },
    { name: 'oidcIssuer', value: config.oidcIssuer },
    { name: 'apiUrl', value: config.apiUrl },
    { name: 'redirectUri', value: config.redirectUri },
    { name: 'authDomain', value: config.authDomain },
    { name: 'loginDomain', value: config.loginDomain },
  ];

  const missing = configChecks.filter(check => !check.value);

  if (missing.length > 0) {
    throw new Error(`Missing required configuration values: ${missing.map(m => m.name).join(', ')}`);
  }

  // Validate domain names (should not include protocol or path)
  if (config.authDomain.includes('://') || config.authDomain.includes('/')) {
    throw new Error('authDomain should be a domain name only (e.g., "auth.agion.dev"), not a URL');
  }
  if (config.loginDomain.includes('://') || config.loginDomain.includes('/')) {
    throw new Error('loginDomain should be a domain name only (e.g., "login.agion.dev"), not a URL');
  }

  // Validate URLs
  try {
    new URL(config.authAuthority);
    new URL(config.oidcIssuer);
    new URL(config.apiUrl);
    new URL(config.redirectUri);
  } catch (error) {
    throw new Error(`Invalid URL in configuration: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// Feature flag utilities
export const isFeatureEnabled = (feature: keyof typeof config.featureFlags): boolean => {
  return config.featureFlags[feature] ?? false;
};

export const isDevelopment = (): boolean => config.environment === 'development' || (import.meta.env.DEV && import.meta.env.NODE_ENV !== 'production');
export const isProduction = (): boolean => config.environment === 'production' && !import.meta.env.DEV;
export const isStaging = (): boolean => config.environment === 'staging';

// Initialize configuration validation on load
if (typeof window !== 'undefined') {
  try {
    validateConfig();
  } catch (error) {
    console.error('Configuration validation failed:', error);
  }
}